import React, { useCallback } from 'react';
import { Box } from '@mui/material';
import { SelectionProvider } from '../../contexts/SelectionContext';
import { useSelection } from '../../hooks/useSelection';
import { useTranslation } from 'react-i18next';

/**
 * AlertCardCollectionProvider - Manages selection state and provides context for alert cards
 * 
 * This component:
 * 1. Manages selection state using useSelection hook
 * 2. Provides selection context to all child alert cards
 * 3. Handles both single and bulk alert actions
 * 4. Wraps children with selection context
 */
const AlertCardCollectionProvider = ({ 
  children,
  onAlertAction,           // Handler for alert actions (not_broken, broken, resolved)
  onDataRefresh,          // Called after successful operations
  onOperationSuccess,     // Success notification callback
  onOperationError,       // Error notification callback
  clientId
}) => {
  const { t } = useTranslation();
  const selection = useSelection();
  
  // Handle action for single alert
  const handleSingleAlertAction = useCallback(async (alertId, action, metadata = {}) => {
    
    try {
      const result = await onAlertAction(alertId, action, metadata);
      if (result && result.success) {
        onOperationSuccess?.(result.message || `Alert ${action} successful`);
        await onDataRefresh?.();
      } else if (result && !result.success) {
        onOperationError?.(result.message || `Failed to ${action} alert`);
      }
    } catch (error) {
      onOperationError?.(t('alertActionFailed') || `Failed to ${action} alert`);
    }
  }, [onAlertAction, onDataRefresh, onOperationSuccess, onOperationError, t]);
  
  // Handle action for multiple alerts
  const handleBulkAlertAction = useCallback(async (alertIds, action, metadata = {}) => {
    
    try {
      // Pass all IDs at once - matches experiment pattern
      const result = await onAlertAction(alertIds, action, metadata);
      
      if (result.success) {
        const actionText = action === 'not_broken' ? 'marked as working' : 
                          action === 'mark_as_broken' ? 'marked as broken' :
                          action === 'resolved' ? 'resolved' : action;
        onOperationSuccess?.(
          alertIds.length === 1 
            ? `Alert ${actionText}` 
            : `${alertIds.length} alerts ${actionText}`
        );
      } else {
        onOperationError?.(
          result.message || (alertIds.length === 1
            ? `Failed to update alert`
            : `Failed to update ${alertIds.length} alerts`)
        );
      }
      
      // Refresh data once after operation
      await onDataRefresh?.();
      // Clear selection after bulk operation
      selection.clearSelection();
      
      return result;
    } catch (error) {
      onOperationError?.(t('bulkOperationFailed') || 'Bulk operation failed');
      return { success: false, error: error.message };
    }
  }, [onAlertAction, onDataRefresh, onOperationSuccess, onOperationError, selection, t]);
  
  // Create context value
  const contextValue = {
    ...selection,
    onSingleAlertAction: handleSingleAlertAction,
    onBulkAlertAction: handleBulkAlertAction,
    onDataRefresh,
    clientId
  };
  
  return (
    <SelectionProvider value={contextValue}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: '12px' 
      }}>
        {children}
      </Box>
    </SelectionProvider>
  );
};

export default AlertCardCollectionProvider;